import { createSelector } from "reselect"
import { get, find, findKey, chain, keyBy, map } from "lodash"
import { numberFormatter } from "helpers/formatters/numbers"
import { getFactorsMetadata as getRawFactorsMetadata } from "selectors/factors"
import { getContentSets } from "selectors/contentSets"
import { getFavoriteFactors } from "modules/portfolio-root/selectors/portfolios"
import { tableDownloadDataReducer } from "helpers/comparison"
import { getSelectedMetric, getRawFactorsCategories } from "./metrics"
import { getData0, getData1, getData2, getData3, getData4 } from "./data"
import { getItems } from "./items"

const getSelectedDate = (_, props) => props.selectedDate
const getIsSingleDate = (_, props) => props.hideGraph
const getContentSetId = (_, props) => get(props, "contentSetId")

function getDailyData(selectedDate, data) {
  const date = find(data, d => d.date === selectedDate)
  if (!date) {
    return null
  }

  return {
    factors: chain(date)
      .get("factors")
      .keyBy("id")
      .mapValues("net")
      .value(),
  }
}

const getDailyData0 = createSelector([getSelectedDate, getData0], getDailyData)
const getDailyData1 = createSelector([getSelectedDate, getData1], getDailyData)
const getDailyData2 = createSelector([getSelectedDate, getData2], getDailyData)
const getDailyData3 = createSelector([getSelectedDate, getData3], getDailyData)
const getDailyData4 = createSelector([getSelectedDate, getData4], getDailyData)

const getFactorsMetadata = createSelector(
  [getRawFactorsMetadata, getContentSets, getContentSetId],
  (factorsMetadata, contentSets, contentSetId) => {
    if (contentSetId) {
      const contentSet = find(contentSets, ({ id }) => id === contentSetId)
      if (contentSet) {
        return keyBy(contentSet.factors, "id")
      }
    }
    return factorsMetadata
  },
)

const getFactorsData = createSelector(
  [getSelectedDate, getRawFactorsCategories, getFavoriteFactors, getData0, getData1, getData2, getData3, getData4],
  (selectedDate, categories, favoriteFactors, data, data1, data2, data3, data4) => {
    const date = find(data, d => d.date === selectedDate)
    if (!date) {
      return null
    }

    // Get all the other factors for the other items at the selected date, flattened. We'll use that to fill
    // the top X factors per categories if we won't have at least X factors returned on the main item
    const allOtherDatesFactors = chain([data1, data2, data3, data4])
      .map(dataDates => chain(dataDates)
        .find(d => d.date === selectedDate)
        .get("factors")
        .value())
      .compact()
      .flatten()
      .value()

    const allFactors = chain(date)
      .get("factors")
      .map(f => ({ ...f, priorityOrder: 1 }))
      .concat(allOtherDatesFactors)
      .uniqBy(item => `${item.id}${item.category}`)
      .value()

    return chain(categories)
      .filter(category => !category.singleFactor)
      .map(category => ({
        id: category.id,
        factors: chain(allFactors)
          .filter(f => f.category === category.id)
          .map(factor => ({
            label: factor.name,
            id: factor.id,
            value: factor.net,
            priorityOrder: factor.priorityOrder,
            fn: item => numberFormatter(get(item, ["factors", factor.id], 0)),
            favorite: !!favoriteFactors[factor.id],
          }))
          .value(),
      }))
      .keyBy("id")
      .mapValues("factors")
      .value()
  },
)

export const getDetailsItems = createSelector(
  [getFactorsMetadata, getSelectedMetric, getIsSingleDate],
  (factors, selectedMetric, isSingleDate) => {
    const items = [...(factors[selectedMetric.id] ? [{
      items: [{
        label: selectedMetric.label,
        hide: isSingleDate,
        fn: item => numberFormatter(get(item, ["factors", selectedMetric.id], 0)),
      }],
    }] : []),
    ]
    return items
  },
)

export const getDetailsData = createSelector(
  [getDailyData0, getDailyData1, getDailyData2, getDailyData3, getDailyData4],
  (data0, data1, data2, data3, data4) => ([data0, data1, data2, data3, data4]),
)

export const getFactorsCategories = createSelector(
  [getRawFactorsCategories, getFactorsData, getFactorsMetadata, getFavoriteFactors],
  (categories, factors, factorsMeta, favoriteFactors) => chain(categories)
    .map((category) => ({
      ...category,
      ...(category.singleFactor && {
        getter: item => numberFormatter(get(item, ["factors", findKey(factorsMeta, f => f.category === category.id)])),
      }),
      factors: chain(factors)
        .get(category.id)
        .map(factor => ({
          ...factor,
          getter: factor.fn,
          favorite: get(favoriteFactors, factor.id, false),
        }))
        .orderBy(["favorite", f => f.priorityOrder || 0, f => Math.abs(f.value)], ["desc", "desc", "desc"])
        .value(),
    }))
    .value(),
)

export const getTableDetailsData = createSelector(
  [getItems, getDetailsData, getDetailsItems],
  (items, data, detailsItems) => tableDownloadDataReducer(items, data, detailsItems, "items"),
)

const getTableFactorsData = createSelector(
  [getItems, getDetailsData, getFactorsCategories],
  (items, data, factorsCategories) => tableDownloadDataReducer(items, data, factorsCategories, "factors"),
)

export const getTableDownloadData = createSelector(
  [getItems, getTableDetailsData, getTableFactorsData],
  (items, tableDetailsData, tableFactorsData) => {
    const headers = ["Metric Group", "Metric", ...map(items, "label")]
    return {
      sheets: [{
        name: "Exposure",
        rows: [
          headers,
          ...tableDetailsData,
          ...tableFactorsData,
        ],
      }],
    }
  },
)
